import React, { useEffect, useMemo, useState } from 'react'
import JoinOtpCertPageView from 'src/pages/JoinPage/JoinOtpCertPage/view'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import useOtpCertForm from 'src/hooks/react-hook-form/useOtpCertForm.hook'
import { FormObjectType, JoinOtpCertFormItemsType } from 'src/types/react-hook-form'
import {
  ktMVNOCheckQueryOption,
  logoutQueryOption,
  otpCertRequestQueryOption,
  otpCertSubmitQueryOption,
  pushTokenUpdateQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import {
  KTMVNOCheckSpace,
  OtpCertRequestSpace,
  OtpCertSubmitSpace,
  PushTokenUpdateSpace,
  ResponseDataType,
  TelecomType,
} from 'src/types/api'
import { useModal } from 'src/provider/ModalContextProvider'
import {
  ERRCODE_SUCCESS,
  ERRCODES_OTPCERTREQUEST,
  ERRCODES_OTPCERTSUBMIT,
} from 'src/service/constant/errcodes'
import { useToast } from 'src/provider/ToastContextProvider'
import { useCookies } from 'react-cookie'
import { useCountdown } from 'src/hooks/useCountDown.hook'

import { DateUtils } from 'src/utils/date'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import usePageNav from 'src/hooks/usePageNav.hook'
import { SubmitErrorHandler } from 'react-hook-form/dist/types/form'
import { encryptPhoneNumber } from 'src/utils'
import { formatMobileKo, NumReplace } from 'src/utils/regExps_util'
import KTMVNOModal from 'src/components/common/Modal/children/KTMVNOModal'
import {
  d30_resignup_dataLayer,
  login_complete_dataLayer,
  otp_request_dataLayer,
} from 'src/utils/gtmDataLayer'
import { Loading } from 'src/components/common/Loading'
import RenderIf from 'src/components/common/RenderIf'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { Bridge } from 'src/utils/bridge'
import useLoginDetector from 'src/hooks/useLoginDetector.hook'
import { useGlobalStore } from 'src/store/zustand'
import styled from 'styled-components'
import usePartnerEntry from 'src/hooks/usePartnerEntry.hook'
import { addFlowBreadcrumb } from 'src/utils/sentry/breadcrumbs'

const JOIN_CERT_SIGNUP_HEADER_TITLE = (
  <>
    <>
      휴대폰쿠폰지갑{' '}
      <span style={{ color: 'var(--red-50)' }}> 이동통신사 제휴 유료 부가서비스</span>
    </>
  </>
)

export interface JoinOtpCertPageProps {
  registerAttribute: Record<keyof JoinOtpCertFormItemsType, Partial<FormObjectType>>
  onOtpCertRequestSubmit: SubmitHandler<Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>>
  onOtpCertRequestError: SubmitErrorHandler<Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>>
  onOtpCertVerifySubmit: SubmitHandler<JoinOtpCertFormItemsType>
  onOtpCertVerifyError: SubmitErrorHandler<JoinOtpCertFormItemsType>
  count: string
  countStop: () => void
  isOtpCertStep: boolean
  isJoinTermsFlow: boolean
  /** 비가입 OTP 이후 또는 제휴 진입 시 이용료 문구 노출 */
  showMonthlyBenefitFee: boolean
}

const JoinOtpCertPage = () => {
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { mainPageNav } = usePageNav()
  const { web } = useChannel()
  const { now, sessionCookieOptions } = DateUtils
  const { c_transId, removeAllCookies } = useCustomCookies()
  const [, setCookie, removeCookie] = useCookies(['auth-key', 'u-tel', 'trans-id'])
  const { methods, registerAttribute } = useOtpCertForm()
  const { setFocus, resetField, watch } = methods
  const pNumberValue = watch('pNumber')
  const { isLogin } = useLoginDetector()
  const [isJoinLoading, setIsJoinLoading] = useState(false)
  const [isRetrySubmit, setIsRetrySubmit] = useState(false)
  const [isOtpCertStep, setIsOtpCertStep] = useState(false)
  const [isJoinTermsFlow, setIsJoinTermsFlow] = useState<boolean>(false)
  const {
    value: { pCode },
    pageValue: {
      joinCert: { isShowMonthlyBenefitFeePartner },
    },
  } = usePartnerEntry()
  const setJoinCertHeaderTitleOverride = useGlobalStore((s) => s.setJoinCertHeaderTitleOverride)

  const countExpiredCallbackFn = () => {
    resetField('certNumber')
    setIsJoinTermsFlow(false)
    removeCookie('trans-id', { path: '/' })
    openModal({
      contents: {
        d: '입력 시간이 만료되었습니다.\n새로운 인증번호를 받아주세요.',
      },
      button: { submit: { t: '확인' } },
    })
  }
  const { countStart, count, countStop } = useCountdown({ countExpiredCallbackFn })

  const { mutateAsync: logoutMutate, isPending: isLogoutPending } =
    useCustomMutation(logoutQueryOption())

  const { mutate: pushTokenUpdateMutate } = useCustomMutation<
    PushTokenUpdateSpace.Request,
    PushTokenUpdateSpace.Response
  >(pushTokenUpdateQueryOption())

  const { mutateAsync: otpRequestMutate, isPending: isOtpRequestPending } = useCustomMutation<
    OtpCertRequestSpace.Request,
    OtpCertRequestSpace.Response
  >(otpCertRequestQueryOption())

  const { mutateAsync: otpSubmit, isPending: isOtpSubmitPending } = useCustomMutation<
    OtpCertSubmitSpace.Request,
    OtpCertSubmitSpace.Response
  >(otpCertSubmitQueryOption())

  const { mutateAsync: ktMVNOCheckMutate } = useCustomMutation<
    KTMVNOCheckSpace.Request,
    KTMVNOCheckSpace.Response
  >(ktMVNOCheckQueryOption())

  const onOtpCertRequestSubmit: SubmitHandler<
    Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>
  > = (data) => {
    const { agency, pNumber } = data
    const digitsPNumber = pNumber.replace(/\D/g, '')
    const submitData = {
      PNumber: digitsPNumber,
      Telecom: Number(agency) as TelecomType,
      ...(!!pCode ? { PCode: pCode } : {}),
    }
    const ktAgency = Number(agency) === 1

    const otpRequestHandler = () => {
      addFlowBreadcrumb('join_attempt', 'request', { handler: 'otpRequest' })
      otpRequestMutate(submitData).then((res: ResponseDataType<OtpCertRequestSpace.Response>) => {
        const { Body, Header } = res
        switch (Header.ErrCode) {
          case ERRCODE_SUCCESS:
            const isJoinMember = Body.JoinMemberYN === 'Y'
            setIsJoinTermsFlow(!isJoinMember)
            setIsOtpCertStep(true)
            setJoinCertHeaderTitleOverride(
              isJoinMember && !isShowMonthlyBenefitFeePartner
                ? null
                : JOIN_CERT_SIGNUP_HEADER_TITLE,
            )
            openToast({ contents: '인증번호를 전송하였습니다.' })
            otp_request_dataLayer()
            countStart(Body.TimeOut)
            setCookie('trans-id', Body.TransID, {
              path: '/',
              expires: now().add(Body.TimeOut, 'seconds').toDate(),
            })
            setTimeout(() => {
              setFocus('certNumber')
            }, 100)
            break
          case ERRCODES_OTPCERTREQUEST.ERROR_30DAYS_LIMIT:
            openModal({
              contents: { d: Header.ErrMsg },
              button: { submit: { t: '확인' } },
            })
            d30_resignup_dataLayer()
            break
          case ERRCODES_OTPCERTREQUEST.ERROR_YEAR_RE_ENTRY_LIMIT:
          case ERRCODES_OTPCERTREQUEST.ERROR_AUTH_MSG_TIME:
            openModal({
              contents: { d: Header.ErrMsg },
              button: { submit: { t: '확인' } },
            })
            break
          case ERRCODES_OTPCERTREQUEST.BLOCKED_USERS:
            openModal({
              contents: { d: Header.ErrMsg },
              button: { submit: { t: '확인', f: () => resetField('pNumber') } },
            })
            break
          case ERRCODES_OTPCERTREQUEST.ERROR_TELECOM:
          case ERRCODES_OTPCERTREQUEST.ERROR_PHONENUMBER:
          case ERRCODES_OTPCERTREQUEST.ERROR_IS_NOT_OPEN_TELECOM:
          default:
            openToast({
              contents: Header.ErrMsg,
            })
            break
        }
      })
    }

    if (ktAgency) {
      ktMVNOCheckMutate(submitData).then((res) => {
        const { Body, Header } = res
        if (Header.ErrCode === ERRCODE_SUCCESS) {
          const MVNO_User = Body?.Type === 1
          if (MVNO_User) {
            openModal({
              contents: {
                children: <KTMVNOModal {...Body} otpRequestHandler={otpRequestHandler} />,
              },
            })
          } else {
            otpRequestHandler()
          }
        }
      })
    } else {
      otpRequestHandler()
    }
  }

  const onOtpCertRequestError: SubmitErrorHandler<Pick<JoinOtpCertFormItemsType, 'pNumber'>> = (
    errors,
    event,
  ) => {
    console.error(errors)
  }

  const onOtpCertVerifySubmit: SubmitHandler<JoinOtpCertFormItemsType> = (data) => {
    const { certNumber } = data

    if (!c_transId && !isRetrySubmit) {
      openToast({ contents: '인증 완료 후 이용해주세요.' })
      return
    }

    if (!c_transId && isRetrySubmit) {
      openToast({ contents: '인증번호를 재전송해주세요.' })
      return
    }

    if (certNumber.length === 0) {
      openToast({ contents: '인증번호를 입력해주세요.' })
      return
    }

    const submitData = {
      TransID: c_transId,
      ApprovalNumber: certNumber,
    }

    otpSubmit(submitData).then((res: ResponseDataType<OtpCertSubmitSpace.Response>) => {
      const { Body, Header } = res
      const { AuthKey, IsJoin, PNumber } = Body

      addFlowBreadcrumb('join_attempt', 'response', {
        handler: 'otpSubmit',
        errCode: res.Header.ErrCode,
        isJoin: IsJoin,
      })

      //키보드 내리기
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }

      const handleJoinFail = (errCode: number, fn?: () => void) => {
        resetField('certNumber')
        openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '확인', f: fn } } })
      }

      switch (Header.ErrCode) {
        case ERRCODE_SUCCESS:
          if (!IsJoin) return openToast({ contents: '잘못된 접근입니다.' })
          setIsJoinLoading(true)

          removeCookie('trans-id', { path: '/' })
          setCookie('u-tel', encryptPhoneNumber(PNumber), sessionCookieOptions())
          setCookie('auth-key', AuthKey, sessionCookieOptions())
          Bridge.handleReqSaveAuthKey(AuthKey)
          Bridge.getPushToken.req()

          const handleMoveToMain = (pNumber: string, biometricAgree: string) => {
            Bridge.handleReqSignUpLogin(pNumber, biometricAgree || 'N')
            mainPageNav()
            openToast({ contents: '로그인되었습니다.' })
            login_complete_dataLayer()
          }

          if (!web) {
            Bridge.getBiometricAgreeState.req(PNumber, () => handleMoveToMain(PNumber, ''))
            Bridge.getBiometricAgreeState.res(
              (result) => handleMoveToMain(PNumber, result),
              () => handleMoveToMain(PNumber, ''),
            )
          } else {
            handleMoveToMain('', '')
          }
          break
        case ERRCODES_OTPCERTSUBMIT.ERROR_WRONG_APPROVAL_NUMBER:
          handleJoinFail(Header.ErrCode, () => !web && setFocus('certNumber'))
          break
        case ERRCODES_OTPCERTSUBMIT.ERROR_EXPIRED_TIME:
        default:
          handleJoinFail(Header.ErrCode)
          break
        case ERRCODES_OTPCERTSUBMIT.ERROR_EXPIRATION_APPROVAL_NUMBER:
          countStop()
          removeCookie('trans-id', { path: '/' })
          setIsOtpCertStep(false)
          setIsRetrySubmit(true)
          handleJoinFail(Header.ErrCode)
          break
      }
    })
  }

  const onOtpCertVerifyError: SubmitErrorHandler<JoinOtpCertFormItemsType> = (errors, event) => {
    if (!c_transId && errors.pNumber !== undefined) {
      openToast({ contents: errors.pNumber.message as string })
    }
  }

  useEffect(() => {
    return () => {
      setJoinCertHeaderTitleOverride(null)
    }
  }, [setJoinCertHeaderTitleOverride])

  useEffect(() => {
    if (isOtpCertStep && !c_transId) {
      setIsOtpCertStep(false)
    }
  }, [c_transId, isOtpCertStep])

  useEffect(() => {
    if (isShowMonthlyBenefitFeePartner) {
      setJoinCertHeaderTitleOverride(JOIN_CERT_SIGNUP_HEADER_TITLE)
    }

    //초기화
    removeCookie('trans-id', { path: '/' })
    localStorage.clear()
    if (isLogin) {
      logoutMutate({}).finally(() => {
        removeAllCookies()
      })
    }

    setTimeout(() => {
      setFocus('pNumber')
    }, 100)

    Bridge.getPushToken.res((pushToken: string) => {
      pushTokenUpdateMutate({ PushToken: pushToken })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const registerAttributeForView = useMemo((): JoinOtpCertPageProps['registerAttribute'] => {
    return {
      ...registerAttribute,
      agency: { ...registerAttribute.agency, disabled: isOtpCertStep },
      pNumber: {
        ...registerAttribute.pNumber,
        disabled: isOtpCertStep,
        ...(isOtpCertStep
          ? {
              formatDisplayValue: formatMobileKo,
              normalizeDisplayInput: (raw: string) => NumReplace(raw, 11),
            }
          : {}),
      },
    }
  }, [registerAttribute, isOtpCertStep])

  const props: JoinOtpCertPageProps = {
    registerAttribute: registerAttributeForView,
    onOtpCertRequestError,
    onOtpCertRequestSubmit,
    onOtpCertVerifyError,
    onOtpCertVerifySubmit,
    count,
    countStop,
    isOtpCertStep,
    isJoinTermsFlow,
    showMonthlyBenefitFee: (isJoinTermsFlow && !!c_transId) || isShowMonthlyBenefitFeePartner,
  }

  return (
    <FormProvider {...methods}>
      <S.JoinOtpCertScrollRoot>
        <RenderIf
          when={isOtpRequestPending || isOtpSubmitPending || isJoinLoading || isLogoutPending}
        >
          <Loading.Default />
        </RenderIf>
        <JoinOtpCertPageView {...props} />
      </S.JoinOtpCertScrollRoot>
    </FormProvider>
  )
}

export default JoinOtpCertPage

const S = {
  JoinOtpCertScrollRoot: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    flex: 1;
    min-height: 0;
    align-self: stretch;
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    scroll-padding-top: 20px;
    -webkit-overflow-scrolling: touch;
  `,
}
